import { cn } from '@/shadcn-ui/utils';
import { CreditScore, VehiclePlan, InsurancePlan } from '../types';
import { Form } from 'react-router';
import { PDPErrors } from '../types/pdp-action';
import { useState } from 'react';
import CreditAgeChangeDrawerDialog from './credit-age-change-drawer-dialog';
import InsuranceDetailsDrawerDialog from './insurance-details-drawer-dialog';
import InsurancePlanRadioGroup from './insurance-plan-radio-group';
import MileagePackageRadioGroup from './mileage-package-radio-group';
import PDPStickyBar from './pdp/proceed-to-schedule/pdp-sticky-bar';
import Spacer from '@/common/components/spacer';
import usePackageSummary from '@/catalog/hooks/use-package-summary';
import usePDPSelectPlanSubmit from '../hooks/use-pdp-select-plan-submit';
import { AgeRange } from '../types/age-range';
import PDPPlanSelectionHelperDrawerDialog from './pdp-plan-selection-helper-drawer-dialog';
import PDPPlanInfoModalTrigger from './pdp-plan-info-modal-trigger';
import Rule from '@/common/components/rule';

interface PDPBuildMonthlyPackageProps {
  vehiclePlans: VehiclePlan[];
  insurancePlans?: InsurancePlan[];
  mostPopularPlanMileageAmount?: string;
  isOrderable: boolean;
  errors?: PDPErrors;
  // CreditAgeChangeDrawerDialog props
  creditModalOpen: boolean;
  handleCloseCreditModal: () => void;
  handleOpenCreditModal: () => void;
  // Credit
  creditScores: CreditScore[];
  ageRanges?: AgeRange[];
  selectedIsOver25: boolean;
  selectedCreditScoreId?: string;
  selectedAgeRangeId?: string;
  selectedMileagePlan?: number;
  selectedInsurancePlan?: string;
  enableInsuranceChange?: boolean;
  fullstoryPlanEventDelegate: () => void;
  fullstoryContinueEventDelegate: () => void;
  fullstoryInsuranceEventDelegate: () => void;
  price_savings_1200_over_850?: number;
  price_per_mile_850_plan_for_1200_miles?: number;
  price_difference_850_vs_1200?: number;
  mostPopularPlanMileagePercentage?: string;
}

export default function PDPBuildMonthlyPackage({
  vehiclePlans,
  insurancePlans,
  mostPopularPlanMileageAmount,
  isOrderable,
  errors,
  // CreditAgeChangeDrawerDialog props
  creditModalOpen,
  handleCloseCreditModal,
  handleOpenCreditModal,
  // Credit
  creditScores,
  ageRanges,
  selectedIsOver25,
  selectedCreditScoreId,
  selectedAgeRangeId,
  selectedMileagePlan,
  selectedInsurancePlan,
  enableInsuranceChange,
  fullstoryPlanEventDelegate,
  fullstoryContinueEventDelegate,
  fullstoryInsuranceEventDelegate,
  // CDN URL
  price_savings_1200_over_850,
  price_per_mile_850_plan_for_1200_miles,
  price_difference_850_vs_1200,
  mostPopularPlanMileagePercentage,
}: PDPBuildMonthlyPackageProps) {
  const [selectedInsurancePlanId, setSelectedInsurancePlanId] = useState<
    string | undefined
  >(
    selectedInsurancePlan !== undefined
      ? `${insurancePlans?.find((plan) => String(plan.id) === selectedInsurancePlan)?.id}`
      : undefined
  );

  const postSubmitDelegate = () => {
    fullstoryPlanEventDelegate?.();
  };

  const { handleSelectPlan, selectedPlanId } = usePDPSelectPlanSubmit(
    vehiclePlans,
    selectedMileagePlan,
    postSubmitDelegate
  );

  const disableInsurancePlanSelection = selectedPlanId === undefined;

  const packageSummary = usePackageSummary({
    selectedMileagePlanId: selectedPlanId,
    selectedInsurancePlanId,
    vehiclePlans,
    insurancePlans,
  });
  return (
    <>
      <div className="flex flex-col px-[24px]">
        <div className="flex items-center gap-[8px]">
          <h2
            className={cn({
              'text-flexcar-neutral70': !isOrderable,
            })}
          >
            Pick your monthly plan
          </h2>

          {/* Plan Selection Helper */}
          <PDPPlanSelectionHelperDrawerDialog
            showInsuranceSection={false}
            className={cn({ 'opacity-40': !isOrderable })}
            vehiclePlans={vehiclePlans}
            price_savings_1200_over_850={price_savings_1200_over_850}
            price_per_mile_850_plan_for_1200_miles={
              price_per_mile_850_plan_for_1200_miles
            }
            price_difference_850_vs_1200={price_difference_850_vs_1200}
          />
        </div>

        <Spacer variant="height4" />

        <p className="body2">Miles roll over. Change plans anytime.</p>

        <Spacer variant="height4" />

        <div className="flex w-full">
          <div className="flex w-full gap-1">
            <div className="flex w-full flex-col items-start justify-between">
              {/* Credit Age Change Drawer Dialog */}
              <CreditAgeChangeDrawerDialog
                open={creditModalOpen}
                handleOpen={handleOpenCreditModal}
                handleClose={handleCloseCreditModal}
                selectedCreditScoreId={selectedCreditScoreId}
                selectedIsOver25={selectedIsOver25}
                selectedAgeRangeId={selectedAgeRangeId}
                creditScores={creditScores}
                ageRanges={ageRanges}
                className={cn({ 'opacity-40': !isOrderable })}
                key={`credit-age-drawer-dialog-${selectedCreditScoreId}-${selectedIsOver25}`}
              />
            </div>
          </div>
        </div>
      </div>

      <Spacer variant="height16" />

      <Form method="post" className="flex flex-col justify-center">
        <div className="px-[24px]">
          <MileagePackageRadioGroup
            vehiclePlans={vehiclePlans}
            selectedPlanId={selectedPlanId || ''}
            mostPopularPlanMileageAmount={mostPopularPlanMileageAmount}
            onValueChange={(event) => handleSelectPlan(event)}
            disabled={!isOrderable}
            error={errors?.plan}
            variant="no-radio"
            mostPopularPlanMileagePercentage={mostPopularPlanMileagePercentage}
          />
        </div>

        <Spacer variant="height32" />

        <Rule direction="horizontal" className="h-[8px]" color="bg-flexcar-neutral90" />

        <Spacer variant="height24" />

        <div className="flex flex-col px-[24px]">
          <div className="flex items-center gap-[8px]">
            <h2
              className={cn('flex items-center', {
                'text-flexcar-neutral70': disableInsurancePlanSelection || !isOrderable,
              })}
            >
              Choose your protection plan
            </h2>

            <InsuranceDetailsDrawerDialog
              className={cn('h-fit', {
                'opacity-40': disableInsurancePlanSelection || !isOrderable,
              })}
              enableInsuranceChange={enableInsuranceChange}
              triggerVariant="custom"
              customTrigger={
                <PDPPlanInfoModalTrigger
                  helpSmallClassName="h-[24px] w-[24px]"
                  disabled={disableInsurancePlanSelection || !isOrderable}
                />
              }
              insurancePlans={insurancePlans}
            />
          </div>
          <Spacer variant="height4" />
          <p className="body2">Basic liability insurance is always included.</p>
          <Spacer variant="height16" />

          <InsurancePlanRadioGroup
            insurancePlans={insurancePlans}
            selectedInsurancePlanId={selectedInsurancePlanId || ''}
            onValueChange={(event) => setSelectedInsurancePlanId(event)}
            disabled={disableInsurancePlanSelection || !isOrderable}
            error={errors?.plan}
            fullstoryEventDelegate={fullstoryInsuranceEventDelegate}
          />

          {isOrderable ? (
            <PDPStickyBar
              packageSummary={packageSummary}
              fullstoryContinueEventDelegate={fullstoryContinueEventDelegate}
            />
          ) : null}
        </div>
      </Form>
    </>
  );
}
